import { useQuery } from "@tanstack/react-query";
import { signedUrlService } from "@/app/lib/services/signedUrlService";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { SignedUrlType } from "@/app/lib/supabase/types";

const STALE_TIME = 3 * 60 * 1000; // 3 minutes
const GC_TIME = 30 * 60 * 1000; // 30 minutes

export const signedUrlCacheQueryKeys = {
  all: ["signedUrlCache"] as const,
  file: (bucket: string, filePath: string) =>
    [...signedUrlCacheQueryKeys.all, "file", bucket, filePath] as const,
  folder: (bucket: string, folderPath: string) =>
    [...signedUrlCacheQueryKeys.all, "folder", bucket, folderPath] as const,
  batch: (requests: Array<{ bucket: string; filePath: string }>) =>
    [...signedUrlCacheQueryKeys.all, "batch", requests] as const,
};

/**
 * Hook to get a signed URL for a single file
 */
export function useSignedUrlForFile(
  bucket: string | undefined,
  filePath: string | undefined,
  urlType: SignedUrlType = "document",
  enabled = true
) {
  const { selectedCompanyId, selectedCompanyUser } = useAppStore();

  return useQuery({
    queryKey: signedUrlCacheQueryKeys.file(bucket || "", filePath || ""),
    queryFn: async () => {
      if (!bucket || !filePath || !selectedCompanyId || !selectedCompanyUser) {
        throw new Error("Missing required parameters");
      }

      return signedUrlService.getSignedUrl(
        selectedCompanyId,
        selectedCompanyUser.user_id,
        bucket,
        filePath,
        urlType
      );
    },
    enabled:
      enabled && !!bucket && !!filePath && !!selectedCompanyId && !!selectedCompanyUser,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to get signed URLs for all files in a folder
 */
export function useSignedUrlsForFolder(
  bucket: string | undefined,
  folderPath: string | undefined,
  urlType: SignedUrlType = "document",
  enabled = true
) {
  const { selectedCompanyId, selectedCompanyUser } = useAppStore();

  return useQuery({
    queryKey: signedUrlCacheQueryKeys.folder(bucket || "", folderPath || ""),
    queryFn: async () => {
      if (!bucket || !folderPath || !selectedCompanyId || !selectedCompanyUser) {
        throw new Error("Missing required parameters");
      }

      return signedUrlService.getSignedUrlsForFolder({
        companyId: selectedCompanyId,
        userId: selectedCompanyUser.user_id,
        bucket,
        folderPath,
        urlType,
      });
    },
    enabled:
      enabled && !!bucket && !!folderPath && !!selectedCompanyId && !!selectedCompanyUser,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Hook to get signed URLs for multiple files (batch)
 */
export function useSignedUrlsBatch(
  requests: Array<{
    bucket: string;
    filePath: string;
    urlType: SignedUrlType;
  }> = [],
  enabled = true
) {
  const { selectedCompanyId, selectedCompanyUser } = useAppStore();

  return useQuery({
    queryKey: signedUrlCacheQueryKeys.batch(
      requests.map((r) => ({ bucket: r.bucket, filePath: r.filePath }))
    ),
    queryFn: async () => {
      if (requests.length === 0 || !selectedCompanyId || !selectedCompanyUser) {
        return {};
      }

      return signedUrlService.getSignedUrlsBatch({
        companyId: selectedCompanyId,
        userId: selectedCompanyUser.user_id,
        requests,
      });
    },
    enabled: enabled && requests.length > 0 && !!selectedCompanyId && !!selectedCompanyUser,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

/**
 * Legacy hook for backward compatibility - wraps the new cache-aware hook
 */
export function useSignedUrl(
  bucket: string | undefined,
  path: string | undefined,
  enabled = true
) {
  // Determine URL type based on bucket name
  let urlType: SignedUrlType = "document";
  if (bucket?.includes("images")) {
    urlType = "image";
  } else if (bucket?.includes("thumbnails")) {
    urlType = "thumbnail";
  }

  const { data: signedUrl, ...rest } = useSignedUrlForFile(bucket, path, urlType, enabled);

  return {
    data: signedUrl,
    ...rest,
  };
}